// Types and interfaces for the Check-in application

export interface Student {
  id: string;
  name: string;
}

export interface Class {
  id: number;
  name: string;
  description: string;
  day: string;
  time: string;
  tag: string;
  checked_in?: boolean;
}

export interface CheckInResponse {
  success: boolean;
  message: string;
}

// Class Management Types
export interface CreateClassRequest {
  name: string;
  description?: string;
  day: string;
  time: string;
  tag: string;
}

export interface UpdateClassRequest {
  name: string;
  description?: string;
  day: string;
  time: string;
  tag: string;
}

export interface ClassFormData {
  // Form field values
  name: string;
  description: string;
  day: string;
  time: string;
  tag: string;

  // UI state
  errors: {
    name?: string;
    description?: string;
    day?: string;
    time?: string;
    tag?: string;
  };
  touched: {
    name?: boolean;
    description?: boolean;
    day?: boolean;
    time?: boolean;
    tag?: boolean;
  };
  isSubmitting: boolean;
}

// Context State Interface
export interface CheckInState {
  // Student data
  students: Student[];
  filteredStudents: Student[];
  selectedStudent: Student | null;

  // Class data
  availableClasses: Class[];
  selectedClasses: number[];

  // UI state
  searchTerm: string;
  showDropdown: boolean;
  showThankYou: boolean;
  thankYouMessage: string;
  loading: boolean;
  error: string;

}

// Action Types
export enum CheckInActionType {
  SET_STUDENTS = 'SET_STUDENTS',
  SET_FILTERED_STUDENTS = 'SET_FILTERED_STUDENTS',
  SET_SELECTED_STUDENT = 'SET_SELECTED_STUDENT',
  SET_AVAILABLE_CLASSES = 'SET_AVAILABLE_CLASSES',
  SET_SELECTED_CLASSES = 'SET_SELECTED_CLASSES',
  TOGGLE_CLASS_SELECTION = 'TOGGLE_CLASS_SELECTION',
  SET_SEARCH_TERM = 'SET_SEARCH_TERM',
  SET_SHOW_DROPDOWN = 'SET_SHOW_DROPDOWN',
  SET_SHOW_THANK_YOU = 'SET_SHOW_THANK_YOU',
  SET_THANK_YOU_MESSAGE = 'SET_THANK_YOU_MESSAGE',
  SET_LOADING = 'SET_LOADING',
  SET_ERROR = 'SET_ERROR',
  RESET_FORM = 'RESET_FORM',
  CLEAR_STUDENT_SELECTION = 'CLEAR_STUDENT_SELECTION'
}

// Action Interfaces
export interface SetStudentsAction {
  type: CheckInActionType.SET_STUDENTS;
  payload: Student[];
}

export interface SetFilteredStudentsAction {
  type: CheckInActionType.SET_FILTERED_STUDENTS;
  payload: Student[];
}

export interface SetSelectedStudentAction {
  type: CheckInActionType.SET_SELECTED_STUDENT;
  payload: Student | null;
}

export interface SetAvailableClassesAction {
  type: CheckInActionType.SET_AVAILABLE_CLASSES;
  payload: Class[];
}

export interface SetSelectedClassesAction {
  type: CheckInActionType.SET_SELECTED_CLASSES;
  payload: number[];
}

export interface ToggleClassSelectionAction {
  type: CheckInActionType.TOGGLE_CLASS_SELECTION;
  payload: number;
}

export interface SetSearchTermAction {
  type: CheckInActionType.SET_SEARCH_TERM;
  payload: string;
}

export interface SetShowDropdownAction {
  type: CheckInActionType.SET_SHOW_DROPDOWN;
  payload: boolean;
}

export interface SetShowThankYouAction {
  type: CheckInActionType.SET_SHOW_THANK_YOU;
  payload: boolean;
}

export interface SetThankYouMessageAction {
  type: CheckInActionType.SET_THANK_YOU_MESSAGE;
  payload: string;
}

export interface SetLoadingAction {
  type: CheckInActionType.SET_LOADING;
  payload: boolean;
}

export interface SetErrorAction {
  type: CheckInActionType.SET_ERROR;
  payload: string;
}

export interface ResetFormAction {
  type: CheckInActionType.RESET_FORM;
}

export interface ClearStudentSelectionAction {
  type: CheckInActionType.CLEAR_STUDENT_SELECTION;
}

// Union type for all actions
export type CheckInAction =
  | SetStudentsAction
  | SetFilteredStudentsAction
  | SetSelectedStudentAction
  | SetAvailableClassesAction
  | SetSelectedClassesAction
  | ToggleClassSelectionAction
  | SetSearchTermAction
  | SetShowDropdownAction
  | SetShowThankYouAction
  | SetThankYouMessageAction
  | SetLoadingAction
  | SetErrorAction
  | ResetFormAction
  | ClearStudentSelectionAction;

// Context Interface
export interface CheckInContextType {
  state: CheckInState;
  dispatch: React.Dispatch<CheckInAction>;
  // Action creators
  setStudents: (students: Student[]) => void;
  setSelectedStudent: (student: Student | null) => void;
  setAvailableClasses: (classes: Class[]) => void;
  toggleClassSelection: (classId: number) => void;
  setSearchTerm: (term: string) => void;
  setShowDropdown: (show: boolean) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string) => void;
  resetForm: () => void;
  clearStudentSelection: () => void;
  showThankYouMessage: (message: string) => void;
  hideThankYouMessage: () => void;
  // Timer management
  resetInteractionTimer: () => void;
}